package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 읽기 결과(#30). **빈 목록이 "없음"인지 "못 읽음"인지 화면이 구분하는 유일한 통로다.**
 *
 * 기존 읽기 계약은 복구 가능한 실패를 빈 리스트로 접었다([com.elan.edubridge.shared.data.datasource.EduConnectDataSource]
 * 실패 규약). 그 규칙 자체는 옳다 — Swift가 계약을 구현하므로 경계를 넘는 Kotlin 예외에는 복구
 * 지점이 없다. 대가는 네트워크 끊김과 "아직 공지가 없어요"가 **같은 화면으로 렌더**되는 것이었다.
 * 여기서 바뀌는 것은 예외를 던진다는 게 아니라 **실패를 잃지 않는다**는 것뿐이다.
 *
 * [CommandFailure]를 재사용한다 — 읽기 전용 enum을 새로 만들면 쓰기와 읽기가 같은 사건
 * (`PERMISSION_DENIED`)을 서로 다른 이름으로 부르게 된다.
 *
 * 단건 조회도 이 타입을 쓴다(0개 또는 1개). "문서 없음"은 [isEmptySuccess]이고 "못 읽음"은
 * [failure]라, 두 경우를 null 하나로 뭉개던 `PostData?` 계약의 결함이 여기서는 재발하지 않는다.
 */
@Serializable
data class ReadResult<T>(
    val items: List<T> = emptyList(),
    val failure: CommandFailure? = null,
) {
    val isSuccess: Boolean get() = failure == null

    /**
     * 화면이 **빈 상태 문구를 그려도 되는 유일한 조건**이다.
     * `items.isEmpty()`만 보고 빈 상태를 그리면 #30이 그대로 재발한다.
     */
    val isEmptySuccess: Boolean get() = failure == null && items.isEmpty()

    /**
     * 목록은 왔는데 실패도 있는 경우 — 학원별 병렬 조회에서 일부만 실패한 상태다.
     * 화면은 목록을 그리고 그 위에 "일부를 불러오지 못했어요" 배너를 얹는다.
     */
    val isPartialFailure: Boolean get() = failure != null && items.isNotEmpty()

    /** 단건 조회용. [isEmptySuccess]와 함께 봐야 "없음"과 "못 읽음"이 갈린다. */
    val item: T? get() = items.firstOrNull()

    companion object {
        /** 성공. 기존 메서드에 위임하는 기본 구현이 쓴다. */
        fun <T> of(items: List<T>): ReadResult<T> = ReadResult(items = items)

        fun <T> failed(failure: CommandFailure, partial: List<T>): ReadResult<T> =
            ReadResult(items = partial, failure = failure)
    }
}
